import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { ISubscriptionPlan, ISubscriptionType } from '@/Module/Subscription/Model/DataModel';
import {
  checkCourseHasSubscriptionV2Action,
  getCalcPriceSubscriptionAction,
  getSubscriptionPlanListAction,
  getSubscriptionsV2UserAction,
  getSubscriptionTypeListAction,
  getTransactionUserAction,
  postSubscriptionRequestAction,
  rubikaPaymentVerifyAction,
} from '@/Module/Subscription/Store/Action';
import { shadPaymentVerifyAction } from '@/Module/Subscription/Store/Action/shadPaymentVerifyAction';
import { subscriptionsV2UserAdapter, transactionUserAdapter } from '@/Module/Subscription/Store/Adapter';

const SubscriptionSlice = createSlice({
  name: 'subscription',
  initialState: {
    paymentIsLoading: false,
    CheckPrice: false,
    isLoading: false,
    typeList: [] as ISubscriptionType[],
    planList: [] as ISubscriptionPlan[],
    subscriptionsV2User: subscriptionsV2UserAdapter.getInitialState({
      isLoading: false,
      total: 0,
    }),
    transactionUser: transactionUserAdapter.getInitialState({
      isLoading: false,
      total: 0,
      page: 0,
      rows: 8,
    }),
    selectedCourseAccess: true,
    guestPaymentSuccess: false,
    mciCouponInfo: '',
  },

  reducers: {
    setPage: (state, { payload }: PayloadAction<number>) => {
      state.transactionUser.page = payload;
    },
    nextPage: state => {
      state.transactionUser.page += 1;
    },
  },

  extraReducers: builder => {
    builder
      .addCase(getSubscriptionTypeListAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(getSubscriptionTypeListAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(getSubscriptionTypeListAction.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.typeList = payload;
      });

    builder
      .addCase(getSubscriptionPlanListAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(getSubscriptionPlanListAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(getSubscriptionPlanListAction.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.planList = payload.data?.sort((a, b) => a.sortOrder - b.sortOrder) ?? [];
      });

    builder
      .addCase(getSubscriptionsV2UserAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(getSubscriptionsV2UserAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(getSubscriptionsV2UserAction.fulfilled, (state, { payload }) => {
        state.subscriptionsV2User.isLoading = false;
        subscriptionsV2UserAdapter.setAll(state.subscriptionsV2User, payload.data);
        state.subscriptionsV2User.total = payload.total;
      });
    builder
      .addCase(postSubscriptionRequestAction.pending, state => {
        state.paymentIsLoading = true;
      })
      .addCase(postSubscriptionRequestAction.rejected, state => {
        state.paymentIsLoading = false;
      })
      .addCase(postSubscriptionRequestAction.fulfilled, state => {
        state.paymentIsLoading = false;
      });
    builder
      .addCase(getCalcPriceSubscriptionAction.pending, state => {
        state.CheckPrice = true;
      })
      .addCase(getCalcPriceSubscriptionAction.rejected, state => {
        state.CheckPrice = false;
      })
      .addCase(getCalcPriceSubscriptionAction.fulfilled, state => {
        state.CheckPrice = false;
      });
    builder
      .addCase(getTransactionUserAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(getTransactionUserAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(getTransactionUserAction.fulfilled, (state, { payload }) => {
        state.transactionUser.isLoading = false;
        if (state.transactionUser.page === 0) {
          transactionUserAdapter.setAll(state.transactionUser, payload.data);

          state.transactionUser.total = payload.total;
        } else {
          transactionUserAdapter.addMany(state.transactionUser, payload.data);
        }
      });
    builder.addCase(checkCourseHasSubscriptionV2Action.fulfilled, (state, { payload }) => {
      state.selectedCourseAccess = payload;
    });
    builder.addCase(rubikaPaymentVerifyAction.fulfilled, (state, { payload }) => {
      state.guestPaymentSuccess = payload;
    });
    builder.addCase(shadPaymentVerifyAction.fulfilled, (state, { payload }) => {
      if (!payload) {
        state.guestPaymentSuccess = false;
        state.mciCouponInfo = '';
        return;
      }
      state.guestPaymentSuccess = payload.status === 'paid';
      state.mciCouponInfo = payload.mciCouponInfo?.code ?? '';
    });
  },
});

export const { nextPage: transactionNextPageAction } = SubscriptionSlice.actions;

export default SubscriptionSlice;
